import { Box, Button, Flex, Input, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import { CSVLink } from "react-csv";
import dayjs from "dayjs";
import { brandPalette } from "../../../constants/brandPalette";
import CTable from "../../../components/Table/table";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import {
  attendanceReportRequest,
  centerHwRequest,
} from "../../../modules/admin-modules/reports/Actions";
import { getAllLecturesRequest } from "../../../modules/admin-modules/lectures/Actions";
import StudentReport from "../../../containers/admin/students/models/student-report";
import { openStudentReportModel } from "../../../modules/admin-modules/models/Actions";

const focusRing = "0 0 0 1px rgba(91, 13, 22, 0.22)";
const selectBase = adminSelectStyles({ menuZIndex: 1600 });
const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};
const cardSx = {
  border: "1px solid",
  borderColor: "var(--border-subtle)",
  bg: "var(--surface-raised)",
  borderRadius: "18px",
  boxShadow: "0 18px 55px rgba(0,0,0,0.12)",
  color: "var(--text-primary)",
};

const headers = [
  { label: "name", key: "csv_name" },
  { label: "group_name", key: "group_name" },
  { label: "phone", key: "phone" },
  { label: "payment_exception", key: "payment_exception" },
];

const AttendanceReport = () => {
  const dispatch = useDispatch();
  const [lecId, setLecId] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [size, setPerPage] = useState(10);
  const setPage = () => {};

  const user = useSelector((state) => state.user.userData);
  const studentReportModel = useSelector((state) => state.model.isOpen18);
  const lectures = useSelector((state) => state.lectures.lectures);
  const attendancesReport = useSelector((state) => state.reports.attendancesReport);
  const isLoading = useSelector((state) => state.reports.isLoading);

  const acceptEvaluation =
    "Asquera - " +
    user?.subject?.name +
    " مراجعة الواجب \n برجاء العلم أن واجب الطالب هذا الاسبوع كامل وتمت مراجعته في السنتر \n شكرا لكم";
  const rejectEvaluation =
    "Asquera - " +
    user?.subject?.name +
    " مراجعة الواجب \n برجاء العلم أن واجب الطالب هذا الاسبوع ناقص برجاء التأكيد على حل الواجب كاملا في المرة القادمة \n شكرا لكم";

  useEffect(() => {
    if (lecId) dispatch(attendanceReportRequest(lecId, date));
  }, [lecId, date, dispatch]);

  useEffect(() => {
    dispatch(getAllLecturesRequest());
  }, [dispatch]);

  const lecOptions = useMemo(
    () =>
      (lectures || []).map((data) => ({
        value: data.id,
        label: data.title,
      })),
    [lectures]
  );

  const Data = useMemo(
    () =>
      (attendancesReport || []).map((data, index) => ({
        ...data,
        index: `#${index + 1}`,
        id: data?.id,
        csv_name: data?.name,
        name: (
          <Text
            cursor="pointer"
            color={brandPalette.primary}
            fontWeight="700"
            _hover={{ textDecoration: "underline" }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedStudent(data);
              dispatch(openStudentReportModel());
            }}
          >
            {data?.name}
          </Text>
        ),
        group_name: data?.group?.name,
        phone: data?.phone,
        payment_exception: data?.payment_exception ? data?.payment_exception?.name : "None",
        center_hw_accept: (
          <Button
            size="sm"
            borderRadius="lg"
            bg="rgba(56, 161, 105, 0.12)"
            color="#276749"
            fontWeight="800"
            _hover={{ bg: "rgba(56, 161, 105, 0.22)" }}
            onClick={(e) => {
              dispatch(
                centerHwRequest({
                  student_id: data?.id,
                  evaluation: acceptEvaluation,
                })
              );
              e.currentTarget.innerText = "✔";
            }}
          >
            Complete
          </Button>
        ),
        center_hw_reject: (
          <Button
            size="sm"
            borderRadius="lg"
            bg="rgba(155, 44, 44, 0.12)"
            color="#9B2C2C"
            fontWeight="800"
            _hover={{ bg: "rgba(155, 44, 44, 0.22)" }}
            onClick={(e) => {
              dispatch(
                centerHwRequest({
                  student_id: data?.id,
                  evaluation: rejectEvaluation,
                })
              );
              e.currentTarget.innerText = "X";
            }}
          >
            Incomplete
          </Button>
        ),
      })),
    [attendancesReport, acceptEvaluation, rejectEvaluation, dispatch]
  );

  const columns = [
    { Header: "#", accessor: "index" },
    { Header: "Student", accessor: "name" },
    { Header: "Group", accessor: "group_name" },
    { Header: "Phone", accessor: "phone" },
    { Header: "Payment exception", accessor: "payment_exception" },
    { Header: "HW complete", accessor: "center_hw_accept" },
    { Header: "HW incomplete", accessor: "center_hw_reject" },
  ];

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={6}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Attendance report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Students present at the selected center session. Click a name for the student file.
            </Text>
          </Box>
          <LogoutMenu />
        </Flex>

        <Flex gap={4} mb={6} flexWrap="wrap" align={{ base: "stretch", md: "flex-end" }}>
          <Box flex="1" minW={{ base: "100%", md: "220px" }} maxW={{ md: "320px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Center
            </Text>
            <ReactSelect
              options={lecOptions}
              isMulti={false}
              onChange={(opt) => setLecId(opt?.value ?? "")}
              styles={selectStyles}
              placeholder="Select center"
              value={lecOptions.find((o) => o.value === lecId) || null}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              isClearable
            />
          </Box>
          <Box minW={{ base: "100%", md: "200px" }} maxW={{ md: "240px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Date
            </Text>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(dayjs(e.target.value).format("YYYY-MM-DD"))}
              borderRadius="14px"
              borderColor="var(--border-subtle)"
              bg="var(--surface-muted)"
              color="var(--text-primary)"
              fontWeight="600"
              _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
            />
          </Box>
          {Data.length > 0 ? (
            <CSVLink
              headers={headers}
              data={Data}
              filename={`attendance-report-${date}.csv`}
              target="_blank"
            >
              <Button
                size="sm"
                h="44px"
                borderRadius="lg"
                bg={brandPalette.secondary}
                color="white"
                fontWeight="800"
                px={6}
                _hover={{ bg: brandPalette.primary }}
              >
                Export CSV
              </Button>
            </CSVLink>
          ) : null}
        </Flex>

        <Box {...cardSx} p={{ base: 4, md: 6 }}>
          {!lecId ? (
            <Text fontWeight="600" color="var(--text-muted)">
              Select a center to load attendance.
            </Text>
          ) : (
            <CTable
              selectedData={() => {}}
              Columns={columns}
              Data={Data}
              Actions
              currentpage={pageNumber}
              setPage={setPage}
              setPageNumber={setPageNumber}
              setPerPage={setPerPage}
              totalPage={Math.ceil((Data.length || 0) / size) || 1}
              isLoading={isLoading}
              perPage={size}
              noSearchBar
              noFilter
              noSecondSearchBar
              wrapperProps={{
                borderWidth: "1px",
                borderColor: "var(--border-subtle)",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            />
          )}
        </Box>
      </Box>

      {studentReportModel && selectedStudent ? (
        <StudentReport
          selectedData={selectedStudent}
          student_id={selectedStudent?.id}
        />
      ) : null}
    </Layout>
  );
};

export default AttendanceReport;
